import { motion, useInView, useReducedMotion } from "framer-motion";
import { useRef } from "react";

/**
 * MaskedLine — masked line reveal that fires immediately on mount.
 * The line-mask parent has overflow-hidden. On reduced-motion, we skip the
 * translate and only render the content in-place (always visible).
 */
export function MaskedLine({ children, delay = 0, className = "", as: Tag = "span" }) {
  const reduced = useReducedMotion();
  return (
    <Tag className={`line-mask ${className}`}>
      <motion.span
        initial={reduced ? false : { y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
        style={{ display: "inline-block" }}
      >
        {children}
      </motion.span>
    </Tag>
  );
}

/**
 * Reveal — a safer fade-and-lift wrapper for below-the-fold content.
 * The child is ALWAYS in its final position; we only nudge opacity from 0 → 1
 * and translate a small Y offset. If the observer never fires (JS failure,
 * reduced motion), the content remains fully readable via CSS fallback.
 */
export function Reveal({ children, delay = 0, y = 20, className = "", as: Tag = "div" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15, margin: "0px 0px -80px 0px" });
  const reduced = useReducedMotion();

  if (reduced) return <Tag ref={ref} className={className}>{children}</Tag>;

  return (
    <Tag ref={ref} className={className}>
      <motion.div
        initial={{ opacity: 0, y }}
        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
        transition={{ duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.div>
    </Tag>
  );
}

/**
 * MaskedLineInView — kept for backward compat with pages already using it.
 * Same safe behaviour as Reveal: opacity fade only, always readable.
 */
export function MaskedLineInView({ children, delay = 0, className = "", as: Tag = "span" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15, margin: "0px 0px -80px 0px" });
  const reduced = useReducedMotion();

  if (reduced) return <Tag ref={ref} className={`line-mask ${className}`}><span style={{ display: "inline-block" }}>{children}</span></Tag>;

  return (
    <Tag ref={ref} className={`line-mask ${className}`}>
      <motion.span
        initial={{ y: "110%" }}
        animate={inView ? { y: "0%" } : { y: "110%" }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
        style={{ display: "inline-block" }}
      >
        {children}
      </motion.span>
    </Tag>
  );
}
